
export interface SettingInfo {
  description: string;
  modes?: string[]; // If omitted, applicable to all
}

export const SETTING_INFO: Record<string, SettingInfo> = {
  // GENERAL DISPLAY
  darkMode: {
    description: "Switch between light and dark user interface themes for better visibility in different environments.",
  },
  showHUD: {
    description: "Toggle the Heads-Up Display which shows real-time simulation data like FPS, speed, and ball count.",
  },
  rgbLighting: {
    description: "Enable dynamic RGB color cycling for various elements in the simulation.",
  },
  cameraFollow: {
    description: "Automatically pan and zoom the camera to keep the primary ball centered.",
  },
  
  // BALL SETTINGS
  initialBalls: {
    description: "The number of balls to spawn when the simulation starts or resets.",
  },
  ballRadius: {
    description: "The size of the balls in pixels. Larger balls have more surface area for collisions.",
  },
  ballMass: {
    description: "Determines how much the ball influences other objects during collisions. Heavier balls are harder to move.",
  },
  ballSpeed: {
    description: "The initial velocity magnitude of the balls when they are spawned.",
  },
  ballColor: {
    description: "The base color of the balls if RGB lighting is disabled.",
  },
  ballGrowthMode: {
    description: "Determines how balls grow: over time, on collision, or synchronized with the total playlist duration.",
  },
  ballGrowthRate: {
    description: "The speed at which balls grow when Growth Mode is active.",
  },
  ballMaxRadius: {
    description: "The maximum size a ball can reach through growth.",
  },
  ballRotationMode: {
    description: "How the ball's visual rotation is determined (e.g., matching its velocity or spinning at a constant rate).",
  },
  ballWeightGrowthEnabled: {
    description: "Gradually increase the ball's mass over time, making it heavier and more impactful.",
  },
  ballMinWeight: {
    description: "The starting mass of the ball when Weight Growth is enabled.",
  },
  ballMaxWeight: {
    description: "The maximum mass a ball can reach through growth.",
  },
  ballWeightGrowthTime: {
    description: "The time in seconds it takes for the ball to reach its maximum weight.",
  },
  ballSquashStretch: {
    description: "A visual effect that deforms the ball based on its velocity and collisions, making it look more dynamic.",
  },
  ballSquashFactor: {
    description: "The intensity of the squash and stretch effect.",
  },
  ballPulseSpeed: {
    description: "The speed at which the ball's size pulses over time.",
  },
  ballFillType: {
    description: "The method used to fill the ball's interior: solid color, radial gradient, linear gradient, or hollow.",
  },
  ballFillOpacity: {
    description: "The transparency of the ball's interior fill.",
  },
  ballGradientInner: {
    description: "The starting color for gradient fills.",
  },
  ballGradientOuter: {
    description: "The ending color for gradient fills.",
  },
  ballTexture: {
    description: "An optional pattern overlay applied to the ball's surface.",
  },
  ballTextureOpacity: {
    description: "The transparency of the texture overlay.",
  },
  ballStrokeWidth: {
    description: "The thickness of the ball's outline.",
  },
  ballStrokeOpacity: {
    description: "The transparency of the ball's outline.",
  },
  ballStrokeColor: {
    description: "The color of the ball's outline.",
  },
  ballGlowRadius: {
    description: "The size of the glow effect around the ball.",
  },
  parallaxWatermarkText: {
    description: "The text to display as a repeating watermark in the background.",
  },
  parallaxWatermarkOpacity: {
    description: "How transparent the watermark text should be.",
  },
  parallaxWatermarkFontSize: {
    description: "The size of the watermark text.",
  },
  parallaxWatermarkRepeatX: {
    description: "How many times the watermark repeats horizontally.",
  },
  parallaxWatermarkRepeatY: {
    description: "How many times the watermark repeats vertically.",
  },
  parallaxWatermarkAngle: {
    description: "The rotation angle of the watermark grid.",
  },
  parallaxWatermarkSpeed: {
    description: "The speed at which the watermark scrolls in the background.",
  },
  ballGlowOpacity: {
    description: "The intensity of the glow effect.",
  },
  ballGlowColor: {
    description: "The color of the glow effect.",
  },
  ballShape: {
    description: "The geometric shape of the balls. This affects their visual appearance and collision behavior.",
  },
  
  // TRAIL SETTINGS
  trailPersistence: {
    description: "When enabled, trail segments never disappear, creating a permanent drawing of the ball's path.",
  },
  trailLength: {
    description: "The number of segments in the ball's trail. Higher values create longer trails.",
  },
  trailInterval: {
    description: "How often (in milliseconds) a new trail segment is generated.",
  },
  trailOpacity: {
    description: "The transparency of the trail segments.",
  },
  trailWidthMultiplier: {
    description: "The thickness of the trail segments relative to the ball size.",
  },
  trailGlowAmount: {
    description: "The intensity of the glow effect around the trail segments.",
  },
  trailOcclusionOptimization: {
    description: "Deletes trail segments that are completely hidden behind newer, larger segments. Recommended for long-running persistent trails.",
  },
  trailDistanceBased: {
    description: "When enabled, trail segments are spawned based on the distance the ball has traveled rather than time. This ensures consistent trail density regardless of ball speed.",
  },
  trailDistanceInterval: {
    description: "The distance (in pixels) the ball must travel before a new trail segment is spawned when Distance Based Spawning is enabled.",
  },
  trailStrokeWidth: {
    description: "The thickness of the outline around each trail segment.",
  },
  trailStrokeColor: {
    description: "The color of the outline around each trail segment.",
  },
  trailApplyBorderToPersistence: {
    description: "Whether to show the outline on trails even when Infinite Persistence is enabled.",
  },
  useTrailRGB: {
    description: "Enable a dynamic rainbow color cycle for the trail fill.",
  },
  trailCustomColor: {
    description: "The color of the trail when Rainbow Fill is disabled.",
  },
  trailStrokeOpacity: {
    description: "The transparency of the trail's outline.",
  },
  trailStartScale: {
    description: "The size of a trail segment when it is first created.",
  },
  trailEndScale: {
    description: "The size of a trail segment just before it fades out.",
  },
  trailMatchBallSize: {
    description: "When enabled, trail segments will exactly match the ball's size at the moment they are created, ignoring the Start and End Scale settings. This is ideal for persistent trails that should remain static even if the ball grows later.",
  },
  trailEnableBorderBreak: {
    description: "Automatically restart the simulation when the trail reaches the container boundary.",
  },
  trailStartPosX: {
    description: "The starting horizontal position of the ball in Trail mode.",
  },
  trailStartPosY: {
    description: "The starting vertical position of the ball in Trail mode.",
  },
  trailStartAngle: {
    description: "The starting movement angle of the ball in Trail mode.",
  },
  trailStartSpeed: {
    description: "The starting speed of the ball in Trail mode.",
  },

  // PHYSICS & DYNAMICS
  gravity: {
    description: "The downward force acting on all balls. Higher values pull balls down faster.",
  },
  restitution: {
    description: "The 'bounciness' of collisions. 1.0 is perfectly elastic, while lower values lose energy on impact.",
  },
  physicsSubsteps: {
    description: "The number of physics calculations per frame. Higher values improve accuracy but increase CPU load.",
  },
  airResistance: {
    description: "A force that slows down balls over time. 0 is no resistance, higher values slow balls faster.",
  },
  maxVelocityLimit: {
    description: "The maximum speed a ball can reach. Prevents balls from flying out of bounds at extreme speeds.",
  },
  minVelocityLimit: {
    description: "The minimum speed a ball will maintain. Prevents balls from coming to a complete stop.",
  },
  antiStallMultiplier: {
    description: "A small force applied to balls that are moving too slowly to keep the simulation dynamic.",
  },
  speedIncreaseMode: {
    description: "Determines if and how the simulation speed increases over time or on collisions.",
  },
  initialSpeed: {
    description: "The starting velocity multiplier for all balls.",
  },
  speedIncreaseRate: {
    description: "The rate at which simulation speed increases.",
  },
  speedAcceleration: {
    description: "How the speed increase rate itself changes over time.",
  },
  masterSpeed: {
    description: "A global multiplier for all movement speeds in the simulation.",
  },
  ballGrowthAcceleration: {
    description: "How the growth rate changes over time.",
  },
  containerShrinkMode: {
    description: "Determines if the outer container should shrink over time or on collisions.",
  },
  containerShrinkRate: {
    description: "How fast the container shrinks.",
  },
  containerShrinkAcceleration: {
    description: "How the shrink rate changes over time.",
  },
  minContainerRadius: {
    description: "The minimum size the container can reach.",
  },

  // RHYTHMIC SYNC
  rhythmicTimeScale: {
    description: "The base speed of the rhythmic pulse and ball movement.",
    modes: ["RHYTHMIC"]
  },
  rhythmicTimeScaleIncreaseMode: {
    description: "How the playback speed changes over time or based on events.",
    modes: ["RHYTHMIC"]
  },
  rhythmicTimeScaleRate: {
    description: "How fast the playback speed increases.",
    modes: ["RHYTHMIC"]
  },
  rhythmicTimeScaleAcceleration: {
    description: "Secondary acceleration for the time scale.",
    modes: ["RHYTHMIC"]
  },
  rhythmicCircleScale: {
    description: "A multiplier for the size of the rhythmic rings.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSyncMode: {
    description: "How the ball's movement synchronizes with the rhythmic pulses.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumEnabled: {
    description: "Visualizes audio frequencies and collision impacts as a dynamic spectrum around the container boundary.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarCount: {
    description: "The number of bars in the audio spectrum visualization.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumMaxHeight: {
    description: "The maximum height of the spectrum bars at peak intensity.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumMinHeight: {
    description: "The minimum height of the spectrum bars when no audio or collision is detected.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarWidth: {
    description: "The thickness of each individual spectrum bar.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarSpacing: {
    description: "The gap between adjacent spectrum bars.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumDuration: {
    description: "How long the spectrum bars remain visible after a collision event.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumColorMode: {
    description: "Determines the color scheme of the spectrum: matching the ball, rainbow, custom, or dynamic pulses.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumGrowRate: {
    description: "How quickly the bars respond to new audio or collision inputs.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumShrinkRate: {
    description: "How quickly the bars decay back to their minimum height.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumAudioSensitivity: {
    description: "Multiplies the intensity of the audio-driven spectrum response.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumCollisionIntensity: {
    description: "Multiplies the intensity of the collision-driven spectrum response.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumCollisionSpread: {
    description: "How many neighboring bars are affected by a single collision point.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumAudioSpatiality: {
    description: "Controls how much the audio affects bars further from the collision point. 0 is global, 1 is fully spatialized.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumGlobalAudio: {
    description: "When enabled, audio frequencies are shown on all bars simultaneously. When disabled, audio only affects bars near recent collision points.",
    modes: ["RHYTHMIC"]
  },
  playlistEnabled: {
    description: "Enable sequential playback of multiple songs in a playlist.",
    modes: ["RHYTHMIC"]
  },
  playlistLoop: {
    description: "Automatically restart the playlist from the beginning after the last song finishes.",
    modes: ["RHYTHMIC"]
  },
  playlistCurrentIndex: {
    description: "The index of the currently playing song in the playlist.",
    modes: ["RHYTHMIC"]
  },
  playlist: {
    description: "The list of songs and their associated presets to be played sequentially.",
    modes: ["RHYTHMIC"]
  },

  // HUD SETTINGS
  hudPosition: {
    description: "Controls where the HUD is anchored on the screen.",
  },
  hudOpacity: {
    description: "The transparency level of the HUD background and text.",
  },
  hudScale: {
    description: "Adjusts the overall size of the HUD elements.",
  },
  hudShowElapsedTime: {
    description: "Shows how long the current simulation has been running.",
  },
  hudShowSpeed: {
    description: "Displays the current speed of the balls.",
  },
  hudShowBallCount: {
    description: "Displays the total number of balls currently in the simulation.",
  },
  hudShowBounceCount: {
    description: "Displays the total number of wall collisions recorded.",
  },
  hudShowRingsLeft: {
    description: "In Escape mode, shows how many rings are remaining.",
    modes: ["ESCAPE"]
  },
  hudShowTimeRemaining: {
    description: "In Escape mode, shows the time left to clear the rings.",
    modes: ["ESCAPE"]
  },
  hudShowFPS: {
    description: "Displays the current frames per second (performance metric).",
  },
  hudShowCurrentRound: {
    description: "In Space Spiral mode, shows the current level or round.",
    modes: ["SPACE_SPIRAL"]
  },
  hudShowContainerRadius: {
    description: "Displays the current size of the container.",
  },
  hudShowBallRadius: {
    description: "Displays the current size of the balls.",
  },
  hudShowGravity: {
    description: "Displays the current gravity strength.",
  },
  hudShowRestitution: {
    description: "Displays the current bounciness factor.",
  },
  showControlIsland: {
    description: "Toggles the floating play and restart buttons at the bottom of the screen.",
  },
  hudShowMode: {
    description: "Display the name of the currently active simulation mode.",
  },

  // BALL PLACEMENT
  ballPlacementMode: {
    description: "How balls are positioned at the start: center, manual coordinates, along the border, or at a specific angle.",
  },
  ballPlacementPosX: {
    description: "The horizontal position of the balls (as a percentage of the container width).",
  },
  ballPlacementPosY: {
    description: "The vertical position of the balls (as a percentage of the container height).",
  },
  ballPlacementBorder: {
    description: "Which side of the container balls should spawn on.",
  },
  ballPlacementAngle: {
    description: "The angle at which balls are placed relative to the center.",
  },
  ballPlacementDistance: {
    description: "The distance from the center at which balls are placed.",
  },
  ballPlacementInitialSpeed: {
    description: "The starting speed of the balls.",
  },
  ballPlacementDirectionAngle: {
    description: "The initial direction in which balls move.",
  },
  ballPlacementRandomDirection: {
    description: "If enabled, balls will move in a random direction at the start.",
  },
  ballPlacementIndividualEnabled: {
    description: "Allows you to configure the starting position and velocity for each ball independently.",
  },

  // GLOBAL VISUALS
  hueCycleSpeed: {
    description: "The speed at which colors shift over time for rainbow effects.",
  },
  shakeIntensityMultiplier: {
    description: "Multiplies the intensity of screen shake effects triggered by collisions.",
  },
  shakeDecayRate: {
    description: "How quickly the screen shake effect fades away after an impact.",
  },
  popDecayRate: {
    description: "How quickly the bounce counter 'pop' animation shrinks back to normal size.",
  },

  // LAYER SORTING
  layer_WALLS: {
    description: "The outer container boundary. Usually rendered first as the background structure.",
  },
  layer_RINGS: {
    description: "Concentric rings used in Escape mode. They sit above the background but below the balls.",
  },
  layer_TRAIL: {
    description: "Visual paths left by moving balls. Can be rendered behind or in front of balls for different effects.",
  },
  layer_BALL: {
    description: "The primary physics objects. Usually rendered near the top for maximum visibility.",
  },
  layer_FX: {
    description: "Visual ripples and impact effects triggered by collisions.",
  },
  layer_BLAST: {
    description: "Shattered pieces and explosion effects when balls break.",
  },
  layer_HUD: {
    description: "User interface elements like timers and counters. Usually rendered on top of everything.",
  },

  // IMPACT FX
  collisionEffect: {
    description: "The visual animation that plays when a ball hits the container wall.",
  },
  collisionEffectDuration: {
    description: "How long the impact animation lasts in milliseconds.",
  },
  collisionEffectParticleCount: {
    description: "The number of particles or elements spawned by the impact effect.",
  },
  collisionEffectScale: {
    description: "A multiplier for the size of the impact effect.",
  },
  collisionEffectGravity: {
    description: "The strength of gravity applied to impact particles.",
  },
  collisionEffectColorMode: {
    description: "How the impact effect is colored: matching the ball, rainbow, or custom colors.",
  },
  collisionEffectColor1: {
    description: "The primary color for custom-colored impact effects.",
  },
  collisionEffectColor2: {
    description: "The secondary color for gradient-based impact effects.",
  },

  // SHAPE SETTINGS
  containerShape: {
    description: "The geometric shape of the outer container. Balls bounce off its inner walls.",
  },
  initialContainerShape: {
    description: "The shape the container resets to when the simulation is restarted.",
  },
  containerRotation: {
    description: "The rotation angle of the outer container in degrees.",
  },
  shapeMorphEnabled: {
    description: "Automatically cycle through different container shapes over time.",
  },
  shapeMorphContinuous: {
    description: "If enabled, the shape will constantly transition. If disabled, it changes at fixed intervals.",
  },
  shapeMorphSpeed: {
    description: "The time in seconds between shape changes when Continuous Shift is disabled.",
  },
  shapeMorphDuration: {
    description: "The duration of the transition animation between two shapes.",
  },

  // ESCAPE MODE
  escapeRingCount: {
    description: "The number of concentric rings the ball must break through to escape.",
    modes: ["ESCAPE"]
  },
  escapeGapSize: {
    description: "The size of the opening in each ring.",
    modes: ["ESCAPE"]
  },
  escapeRotationSpeed: {
    description: "How fast the rings rotate, making the gaps harder to hit.",
    modes: ["ESCAPE"]
  },
  escapeGapAlignment: {
    description: "How the gaps in the rings are positioned relative to each other.",
    modes: ["ESCAPE"]
  },

  // SPAWNER MODE
  spawnOnWallCollision: {
    description: "Spawn a new ball every time a ball hits the container wall.",
    modes: ["SPAWNER"]
  },
  spawnOnBallCollision: {
    description: "Spawn a new ball every time two balls collide.",
    modes: ["SPAWNER"]
  },
  maxBalls: {
    description: "The maximum number of balls allowed in the simulation at once.",
    modes: ["SPAWNER"]
  },

  // SPACE SPIRAL MODE
  spaceSpiralSpeed: {
    description: "The speed at which the spiral rotates.",
    modes: ["SPACE_SPIRAL"]
  },
  spaceSpiralBoxCount: {
    description: "The number of boxes that form the spiral.",
    modes: ["SPACE_SPIRAL"]
  },

  // VISUALS & COLORS
  containerLineWidth: {
    description: "The thickness of the arena borders.",
  },
  borderColor: {
    description: "The color of the arena borders.",
  },
  outerColor: {
    description: "The color of the area outside the arena.",
  },
  innerColor: {
    description: "The background color inside the arena.",
  },
  collisionWaveColor: {
    description: "The color of the waves generated on collision.",
  },
  blastColor: {
    description: "The color of fragments in a victory blast.",
  },
  blastMatchRingColor: {
    description: "If enabled, blast shards will match the color of the ring that was broken.",
  },

  // AUDIO WORKSHOP
  selectedMelody: {
    description: "The current song or melody being played in Rhythmic mode.",
    modes: ["RHYTHMIC"]
  },

  // TITLE & FOOTER SETTINGS
  titleText: {
    description: "The main heading text displayed at the top of the simulation.",
  },
  titleFontFamily: {
    description: "The typeface used for the title text.",
  },
  titleFontWeight: {
    description: "The thickness or boldness of the title font.",
  },
  titleFontSize: {
    description: "The size of the title text in pixels.",
  },
  titleColor: {
    description: "The color of the title text.",
  },
  footerText: {
    description: "The subtext or credits displayed at the bottom of the simulation.",
  },
  footerFontFamily: {
    description: "The typeface used for the footer text.",
  },
  footerFontWeight: {
    description: "The thickness or boldness of the footer font.",
  },
  footerFontSize: {
    description: "The size of the footer text in pixels.",
  },
  footerTextColor: {
    description: "The color of the footer text.",
  },

  // AUDIO WORKSHOP & STORAGE
  melodyName: {
    description: "Assign a name to your custom melody before saving it to the cloud or your library.",
  },
  manualMelodyString: {
    description: "Paste raw frequency lists, JSON rhythm data, or MIDI base64 strings here to create custom songs.",
  },
  uploadMidi: {
    description: "Upload a standard MIDI file (.mid) to automatically convert it into a playable rhythmic sequence.",
  },
  storageTab: {
    description: "Switch between Local Storage (browser-based) and D1 Cloud Storage (persistent across devices).",
  },
  saveName: {
    description: "The name for your current configuration snapshot.",
  },
  exportAll: {
    description: "Download a full backup of all your local and cloud saves as a single JSON file.",
  },
  importAll: {
    description: "Restore your saves from a previously exported JSON backup file.",
  },
  jsonWorkshop: {
    description: "Directly view and edit the raw JSON data of your saves for advanced configuration management.",
  },

  // AUDIO STUDIO
  masterVolume: {
    description: "The overall volume level of the entire application.",
  },
  melodyVolume: {
    description: "The volume level of the background melodies and rhythmic sequences.",
  },
  fxVolume: {
    description: "The volume level of collision sounds and impact effects.",
  },
  audioCutoffEnabled: {
    description: "When enabled, starting a new sound will immediately stop the previous one to prevent audio overlapping.",
  },
  melodyInstrument: {
    description: "The virtual instrument used to play back rhythmic melodies.",
  },
  audioEcho: {
    description: "Enable or disable a stereo delay effect for the melody rack.",
  },
  audioDelayTime: {
    description: "The time interval between echo repetitions in seconds.",
  },
  audioDelayFeedback: {
    description: "Controls how many times the echo repeats. Higher values create longer trails.",
  },
  audioEchoWet: {
    description: "The balance between the dry original sound and the processed echo effect.",
  },
  audioLowPassFreq: {
    description: "The cutoff frequency for the low-pass filter. Lower values create a 'muffled' or 'underwater' sound.",
  },
  audioLowPassResonance: {
    description: "The resonance (Q) peak at the filter's cutoff frequency. Higher values add a 'ringing' quality.",
  },
  audioHighPassFreq: {
    description: "The cutoff frequency for the high-pass filter. Removes low-end frequencies for a 'thin' or 'radio' sound.",
  },
  audioCompThreshold: {
    description: "The decibel level at which the compressor starts reducing the volume of loud peaks.",
  },
  audioCompRatio: {
    description: "The amount of volume reduction applied once the signal exceeds the threshold.",
  },
  audioPitchShift: {
    description: "Shift the pitch of all audio up or down in semitones without changing playback speed.",
  },
  audioCompoundMode: {
    description: "An optimized audio engine designed to handle thousands of simultaneous collisions without performance lag.",
  },
  audioSlidingEnabled: {
    description: "Enable friction-based sounds when balls slide along walls or surfaces.",
  },
  audioSlidingVolume: {
    description: "The maximum volume of the sliding friction sound.",
  },
  audioSlidingPitch: {
    description: "The base pitch of the sliding friction sound.",
  },
  audioSlidingMaxSpeed: {
    description: "The speed at which the sliding sound reaches its maximum volume and pitch.",
  },
  songSliceDuration: {
    description: "The default duration for new slices added to the song timeline.",
  },
  songSliceContinuousMode: {
    description: "When enabled, slices will play sequentially in a loop rather than being triggered individually.",
  },
  audioMutedInSlicer: {
    description: "Mute the main simulation audio while using the song slicer workshop.",
  },
  songSpeedIncreaseEnabled: {
    description: "Gradually increase the playback speed of the song as more collisions occur.",
  },
  songSpeedMax: {
    description: "The maximum playback speed multiplier for the song speed-up effect.",
  },
  songResetEnabled: {
    description: "Automatically reset the song playback speed if no collisions occur for a set period.",
  },
  songResetTimer: {
    description: "The duration of silence required before the song playback speed resets to normal.",
  },
  soundEngineMode: {
    description: "Switch between configuring Collision Bounces (sounds when balls hit objects) and Ring Destructions (sounds when rings are broken).",
  },
  soundSourceType: {
    description: "Choose the source of the sound: Melody (global sequence), FX (pre-set sound effects), or Uploads (your custom audio files).",
  },
  selectedBallSoundEffect: {
    description: "Select a specific high-quality sound effect from the built-in library for ball collisions.",
  },
  userSoundLibrary: {
    description: "Manage your custom uploaded sounds. You can rename, preview, or delete them from the cloud.",
  },
  songConverter: {
    description: "Upload an MP3 file to automatically analyze its rhythm and convert it into a sequence of collision events.",
  },
};
